import {CardinalDirection, MaintenanceIntervalType, CostUnit} from "../../../types/Enums";
import { PhotoModel } from "../../../types/protocols";

// Re-export CostUnit for backwards compatibility with existing imports
export type { CostUnit } from "../../../types/Enums";

export interface DirectionalInterval {
    direction: CardinalDirection;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
}

export interface MaintenanceTemplate {
    id: string;
    name: string;
    description: string;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
    cost: number;
    cost_unit: CostUnit;
    directionalIntervals?: DirectionalInterval[];
    instructionalAttachments?: { [key: string]: PhotoModel };

    // Primary component type this template is designed for
    // Used for suggestions when adding activities
    primaryComponentType: string;

    // Building-specific templates
    // If set, template is only visible to this building
    // undefined = global template visible to all buildings
    buildingKey?: string;

    // Archived: deleted while plans used it. Kept so those activities still
    // resolve, never offered for new use. Both absent on a live template.
    archivedAt?: string; // YYYY-MM-DD hh:mm:ss
    archivedBy?: string; // UID
}

export interface TemplatesByType {
    [key: string]: MaintenanceTemplate[];
}

export interface EditForm {
    name: string;
    description: string;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
    cost: number;
    cost_unit: CostUnit;
    directionalIntervals: DirectionalInterval[];
    instructionalAttachments: { [key: string]: PhotoModel };
    primaryComponentType: string;
    buildingKey?: string;
    /**
     * Pre-minted key for a NEW template, set when attachments were uploaded to
     * the template's Storage path before the template was saved. Consumed by
     * maintenanceTemplateApi.createTemplate — never persisted on the record.
     */
    templateKey?: string;
}

export interface DirectionalLifetime {
    direction: CardinalDirection;
    expectedLifetimeYears: number;
    replacementCost?: number;
}

export interface ComponentLifetime {
    expectedLifetimeYears: number;
    replacementCost?: number;
    costUnit?: CostUnit;
    directionalLifetimes?: DirectionalLifetime[];
}

export function getLifetimeForDirection(
    lifetime: ComponentLifetime,
    direction: CardinalDirection | null
): { expectedLifetimeYears: number; replacementCost?: number } {
    if (!direction || !lifetime.directionalLifetimes) {
        return {
            expectedLifetimeYears: lifetime.expectedLifetimeYears,
            replacementCost: lifetime.replacementCost
        };
    }

    const directionalLifetime = lifetime.directionalLifetimes.find(
        dl => dl.direction === direction
    );

    return {
        expectedLifetimeYears: directionalLifetime?.expectedLifetimeYears || lifetime.expectedLifetimeYears,
        replacementCost: directionalLifetime?.replacementCost ?? lifetime.replacementCost
    };
}

/** Whether the template sets its own interval for the direction, i.e. the one `getIntervalForDirection` returns. */
export function hasIntervalForDirection(
    template: MaintenanceTemplate,
    direction: CardinalDirection | null
): boolean {
    return Boolean(direction && template.directionalIntervals?.some(
        di => di.direction === direction && di.interval_value
    ));
}

export function getIntervalForDirection(
    template: MaintenanceTemplate,
    direction: CardinalDirection | null
): { interval_type: MaintenanceIntervalType; interval_value: number } {
    if (!direction || !template.directionalIntervals) {
        return {
            interval_type: template.interval_type,
            interval_value: template.interval_value
        };
    }

    const directionalInterval = template.directionalIntervals.find(
        di => di.direction === direction
    );

    return {
        interval_type: directionalInterval?.interval_type || template.interval_type,
        interval_value: directionalInterval?.interval_value || template.interval_value
    };
}
